import React, { useContext } from 'react';
import cx from 'utils/classnames';
import Rating from 'components/base/rating/Rating';
import ToolTip from 'components/base/toolTip/ToolTip';
import Wysiwyg from 'components/blocks/wysiwyg/Wysiwyg';
import useAppendQueryParams from 'hooks/custom/useAppendQueryParams';
import { QUERY_PARAMS } from 'consts';
import { GetSchoolListingResults } from 'types';
import ResultCardContext from './ResultCardContext';
import ResultCardLogo from './ResultCardLogo';
import ResultCardLink from './ResultCardLink';
import ResultCardTitle from './ResultCardTitle';
import ResultCardFooter from './ResultCardFooter';
import SchoolDates from './SchoolDates';

const { IMPRESSION_GUID_PARAM } = QUERY_PARAMS;

interface PortalCardProps {
  cardRef: React.RefObject<HTMLElement | undefined>;
  schoolInfo: GetSchoolListingResults['results'][number];
  isLoading?: boolean;
  logoImg?: { url?: string } | string;
  badge?: string;
  isProgramView: boolean;
  currentLink: string;
  onLogoClick: () => void;
  onTitleClick: () => void;
  onCTAclick: () => void;
  onProgramLinkClick: () => void;
  onTotalCountClick: () => void;
}

function getHighlightsHtml(
  highlights: GetSchoolListingResults['results'][number]['highlights']
): string {
  if (!highlights) {
    return '';
  }
  return Array.isArray(highlights) ? highlights.join('') : highlights;
}

export default function PortalCard({
  cardRef,
  schoolInfo,
  isLoading,
  logoImg,
  badge,
  isProgramView,
  currentLink,
  onLogoClick,
  onTitleClick,
  onCTAclick,
  onProgramLinkClick,
  onTotalCountClick,
}: PortalCardProps): React.JSX.Element {
  const { ratingTooltipText } = useContext(ResultCardContext);
  const highlights = getHighlightsHtml(schoolInfo.highlights);
  const selectedProgramName = schoolInfo.selectedProgram?.label;
  const schoolId = schoolInfo?.id?.toString();

  const learnMoreLink = useAppendQueryParams(currentLink, {
    [IMPRESSION_GUID_PARAM]: schoolInfo.impressionGuid,
    click_location: isProgramView
      ? 'best matching card | learn-more'
      : 'school card | learn-more',
  });
  const bestMatchingProgramLink = useAppendQueryParams(currentLink, {
    [IMPRESSION_GUID_PARAM]: schoolInfo.impressionGuid,
    click_location: 'best matching card | best matching link',
  });

  return (
    <article
      ref={cardRef as React.RefObject<HTMLElement>}
      data-testid="portalCard"
      className={cx('portalCard', {
        'portalCard--isLoading': isLoading,
        'portalCard--hasBadge': Boolean(badge),
      })}
    >
      {badge && <div className="portalCard__badge">{badge}</div>}
      <div className="portalCard__body">
        <div className="portalCard__top">
          <ResultCardTitle
            className="portalCard__title"
            impressionGuid={schoolInfo.impressionGuid}
            isProgramView={isProgramView}
            linkUrl={currentLink}
            onClick={onTitleClick}
          >
            {schoolInfo.name}
          </ResultCardTitle>
          <div className="portalCard__rating">
            {isLoading ? null : (
              <Rating rating={schoolInfo.rating || 0} id={schoolId} />
            )}
            <span className="portalCard__ratingLabel">Editor&apos;s Rating</span>
            {ratingTooltipText && (
              <span className="portalCard__ratingToolTip">
                <ToolTip label="More Info" positioning="bottom">
                  <Wysiwyg isClean content={ratingTooltipText} />
                </ToolTip>
              </span>
            )}
          </div>
        </div>
        <div className="portalCard__content">
          <div className="portalCard__logo">
            {
              // @ts-expect-error JS component; inferred props don't match PropTypes
              <ResultCardLogo
                isLoading={isLoading}
                linkUrl={currentLink}
                logoImg={logoImg}
                onClick={onLogoClick}
                impressionGuid={schoolInfo.impressionGuid}
                isProgramView={isProgramView}
              />
            }
          </div>
          <div className="portalCard__details">
            <SchoolDates
              className="portalCard__startDate"
              schoolDates={schoolInfo.schoolDates}
            />
            {highlights && (
              <div className="portalCard__highlights">
                <Wysiwyg content={highlights} />
              </div>
            )}
            {isProgramView && selectedProgramName && (
              <div className="portalCard__program">
                <span className="portalCard__programLabel">
                  Best Matching Program:
                </span>
                <ResultCardLink
                  linkUrl={bestMatchingProgramLink}
                  className="portalCard__programName"
                  onClick={onProgramLinkClick}
                >
                  {selectedProgramName}
                </ResultCardLink>
              </div>
            )}
          </div>
          <div className="portalCard__actions">
            <ResultCardLink
              linkUrl={learnMoreLink}
              className="portalCard__cta"
              onClick={onCTAclick}
            >
              {isProgramView ? 'Visit Site' : 'Find Your Program'}
            </ResultCardLink>
          </div>
        </div>
      </div>
      <div className="portalCard__footer">
        <ResultCardFooter
          description={schoolInfo.selectedProgram?.shortDesc}
          review={schoolInfo.description}
          // @ts-expect-error JS component; inferred props don't match PropTypes
          highlights={schoolInfo.highlights}
          isLoading={isLoading}
          isProgramView={isProgramView}
          onTotalCountClick={onTotalCountClick}
          otherPrograms={schoolInfo.otherPrograms}
          programCount={schoolInfo.programCount}
          cardLink={currentLink}
          schoolId={schoolInfo.id}
          impressionGuid={schoolInfo.impressionGuid}
        />
      </div>
    </article>
  );
}
